import React, { useEffect, useMemo, useState } from 'react';

const defaultSubmenus = {
  shows: [
    { label: 'Current Season', url: '/shows/current/' },
    { label: 'Archive', url: '/shows/archive/' }
  ],
  about: [
    { label: 'The Company', url: '/about/' },
    { label: 'Mission & History', url: '/about/#mission' },
    { label: 'Resources', url: '/resources/' }
  ],
  resources: [
    { label: 'Resources', url: '/resources/' },
    { label: 'Contacts', url: '/contacts/' }
  ],
  world: [
    { label: 'Africa', url: '/tag/africa/' },
    { label: 'Asia', url: '/tag/asia/' },
    { label: 'America', url: '/tag/america/' }
  ]
};

const normalizeKey = (value = '') => {
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

const getSubmenuKey = (item) => {
  const label = normalizeKey(item.label);
  const slug = normalizeKey(item.slug);

  if (slug === 'shows' || label.includes('show') || label.includes('espetac')) {
    return 'shows';
  }

  if (slug === 'about' || label === 'about' || label.includes('sobre')) {
    return 'about';
  }

  if (slug === 'resources' || label === 'resources' || label.includes('recurso')) {
    return 'resources';
  }

  if (slug === 'world' || slug === 'earth' || slug === 'terra' || label.includes('world') || label.includes('earth') || label.includes('terra')) {
    return 'world';
  }

  return null;
};

const resolveChildren = (item) => {
  if (Array.isArray(item.children) && item.children.length > 0) {
    return item.children;
  }

  if (Array.isArray(item.submenu) && item.submenu.length > 0) {
    return item.submenu;
  }

  if (Array.isArray(item.items) && item.items.length > 0) {
    return item.items;
  }

  const key = getSubmenuKey(item);
  return key ? defaultSubmenus[key] : [];
};

const MobileMenuDrawer = ({
  isOpen,
  onClose,
  navigation = [],
  t,
  currentLang,
  onLanguageChange,
  currentTheme,
  onToggleTheme
}) => {
  const [openSections, setOpenSections] = useState({});

  const navWithChildren = useMemo(() => {
    return navigation.map((item, index) => ({
      ...item,
      _key: item.id || item.slug || `${normalizeKey(item.label)}-${index}`,
      _children: resolveChildren(item)
    }));
  }, [navigation]);

  useEffect(() => {
    if (!isOpen) {
      setOpenSections({});
    }
  }, [isOpen]);

  const toggleSection = (itemKey) => {
    setOpenSections((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  return (
    <div
      className={`gh-mobile-drawer-react gh-command-bar-react ${isOpen ? 'is-open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={t('Navigation Menu', 'Navigation Menu')}
    >
      <button
        type="button"
        className="gh-mobile-drawer-backdrop"
        onClick={onClose}
        aria-label={t('Close menu', 'Close menu')}
      />

      <aside className="gh-mobile-drawer-panel gh-command-bar-panel-react">
        <header className="gh-mobile-drawer-header gh-command-bar-header-react">
          <h2>{t('Menu', 'Menu')}</h2>
          <button
            type="button"
            className="gh-mobile-drawer-close"
            onClick={onClose}
            aria-label={t('Close Menu', 'Close Menu')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12" />
              <path d="M18 6l-12 12" />
            </svg>
          </button>
        </header>

        <div className="gh-mobile-drawer-content gh-command-bar-content-react">
          <nav className="gh-mobile-drawer-nav">
            <ul className="gh-command-bar-nav-react">
              {navWithChildren.map((item) => {
                const hasChildren = item._children.length > 0;
                const isExpanded = !!openSections[item._key];

                return (
                  <li key={item._key} className="gh-mobile-drawer-item">
                    <div className="gh-mobile-drawer-row">
                      <a href={item.url} className="gh-command-bar-menu-link" onClick={onClose}>
                        {t(item.label, item.label)}
                      </a>

                      {hasChildren && (
                        <button
                          type="button"
                          className={`gh-mobile-drawer-expand ${isExpanded ? 'is-open' : ''}`}
                          onClick={() => toggleSection(item._key)}
                          aria-label={t('Toggle submenu', 'Toggle submenu')}
                          aria-expanded={isExpanded}
                        >
                          <span className="gh-mobile-drawer-plus" aria-hidden="true">
                            <span className="gh-plus-line gh-plus-line-h" />
                            <span className="gh-plus-line gh-plus-line-v" />
                          </span>
                        </button>
                      )}
                    </div>

                    {hasChildren && (
                      <div className={`gh-mobile-drawer-submenu ${isExpanded ? 'is-open' : ''}`}>
                        <ul>
                          {item._children.map((child, childIndex) => (
                            <li key={`${item._key}-child-${childIndex}`}>
                              <a href={child.url} className="gh-command-bar-sub-link" onClick={onClose}>
                                {t(child.label, child.label)}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <footer className="gh-mobile-drawer-footer">
          <button
            type="button"
            className={`gh-mobile-theme-toggle ${currentTheme === 'dark' ? 'is-dark' : 'is-light'}`}
            onClick={onToggleTheme}
            aria-label={t('Toggle theme', 'Toggle theme')}
          >
            <span className="gh-mobile-theme-track">
              <span className="gh-mobile-theme-thumb">
                <svg className="gh-theme-icon gh-theme-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2" />
                  <path d="M12 20v2" />
                  <path d="M4.93 4.93l1.41 1.41" />
                  <path d="M17.66 17.66l1.41 1.41" />
                  <path d="M2 12h2" />
                  <path d="M20 12h2" />
                  <path d="M4.93 19.07l1.41-1.41" />
                  <path d="M17.66 6.34l1.41-1.41" />
                </svg>
                <svg className="gh-theme-icon gh-theme-moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3c-.14.62-.21 1.27-.21 1.93a8 8 0 0 0 8 8c.66 0 1.31-.07 2-.14z" />
                </svg>
              </span>
            </span>
            <span className="gh-mobile-theme-label">
              {currentTheme === 'dark' ? t('Dark', 'Dark') : t('Light', 'Light')}
            </span>
          </button>

          <div className="gh-mobile-language-switch">
            <button
              type="button"
              className={currentLang === 'PT' ? 'active' : ''}
              onClick={() => onLanguageChange('PT')}
              aria-label="Portuguese"
            >
              PT
            </button>
            <button
              type="button"
              className={currentLang === 'EN' ? 'active' : ''}
              onClick={() => onLanguageChange('EN')}
              aria-label="English"
            >
              EN
            </button>
          </div>
        </footer>
      </aside>

      <style>{`
        .gh-mobile-drawer-react {
          position: fixed;
          inset: 0;
          z-index: 11000;
          pointer-events: none;
        }

        .gh-mobile-drawer-backdrop {
          position: absolute;
          inset: 0;
          border: 0;
          background: rgba(11, 15, 26, 0.34);
          backdrop-filter: blur(2px);
          opacity: 0;
          transition: opacity 0.26s ease;
          cursor: pointer;
        }

        .gh-mobile-drawer-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: min(92vw, 430px);
          height: 100dvh;
          background: linear-gradient(165deg, #f9fafb 0%, #ffffff 44%, #f5f7fb 100%);
          border-left: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: -16px 0 42px rgba(15, 23, 42, 0.2);
          display: flex;
          flex-direction: column;
          transform: translateX(104%);
          transition: transform 0.34s cubic-bezier(0.33, 1, 0.68, 1);
          overflow: hidden;
        }

        .gh-mobile-drawer-react.is-open {
          pointer-events: auto;
        }

        .gh-mobile-drawer-react.is-open .gh-mobile-drawer-backdrop {
          opacity: 1;
        }

        .gh-mobile-drawer-react.is-open .gh-mobile-drawer-panel {
          transform: translateX(0);
        }

        .gh-mobile-drawer-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.1rem 1.1rem;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(14px);
        }

        .gh-mobile-drawer-header h2 {
          margin: 0;
          font-size: 1.35rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #0f172a;
        }

        .gh-mobile-drawer-close {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #fff;
          color: #0f172a;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.2s ease, background 0.2s ease;
        }

        .gh-mobile-drawer-close:hover {
          background: #f1f5f9;
          transform: rotate(90deg);
        }

        .gh-mobile-drawer-close svg {
          width: 18px;
          height: 18px;
        }

        .gh-mobile-drawer-content {
          flex: 1;
          overflow-y: auto;
          padding: 0.75rem 1rem 1.25rem;
        }

        .gh-mobile-drawer-nav ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          gap: 0.55rem;
        }

        .gh-mobile-drawer-item {
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .gh-mobile-drawer-row {
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;
          gap: 0.5rem;
          padding: 0.18rem;
        }

        .gh-mobile-drawer-row > a {
          display: block;
          padding: 1rem 1rem;
          font-size: 1.2rem;
          font-weight: 700;
          color: #111827;
          letter-spacing: -0.01em;
          text-decoration: none;
          border-radius: 10px;
        }

        .gh-mobile-drawer-row > a:active,
        .gh-mobile-drawer-row > a:hover {
          background: #f8fafc;
        }

        .gh-mobile-drawer-expand {
          width: 44px;
          height: 44px;
          margin-right: 0.4rem;
          border: 0;
          border-radius: 10px;
          background: transparent;
          color: #334155;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .gh-mobile-drawer-plus {
          position: relative;
          width: 16px;
          height: 16px;
          transition: transform 0.25s ease;
        }

        .gh-plus-line {
          position: absolute;
          left: 50%;
          top: 50%;
          display: block;
          width: 16px;
          height: 2px;
          margin-left: -8px;
          margin-top: -1px;
          background: currentColor;
          border-radius: 999px;
          transition: transform 0.25s ease, opacity 0.25s ease;
        }

        .gh-plus-line-v {
          transform: rotate(90deg) scaleX(1);
        }

        .gh-mobile-drawer-expand.is-open .gh-mobile-drawer-plus {
          transform: rotate(180deg);
        }

        .gh-mobile-drawer-expand.is-open .gh-plus-line-v {
          transform: rotate(90deg) scaleX(0);
          opacity: 0;
        }

        .gh-mobile-drawer-submenu {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.28s ease;
        }

        .gh-mobile-drawer-submenu.is-open {
          grid-template-rows: 1fr;
        }

        .gh-mobile-drawer-submenu > ul {
          overflow: hidden;
          margin: 0 0.8rem 0.9rem;
          padding: 0.2rem 0.2rem 0;
          border-top: 1px solid rgba(15, 23, 42, 0.08);
          display: grid;
          gap: 0.25rem;
          opacity: 0;
          transform: translateY(-5px);
          transition: opacity 0.24s ease, transform 0.24s ease;
        }

        .gh-mobile-drawer-submenu.is-open > ul {
          opacity: 1;
          transform: translateY(0);
        }

        .gh-mobile-drawer-submenu a {
          display: block;
          padding: 0.8rem 0.55rem;
          border-radius: 8px;
          color: #374151;
          font-size: 0.98rem;
          font-weight: 600;
          text-decoration: none;
        }

        .gh-mobile-drawer-submenu a:hover,
        .gh-mobile-drawer-submenu a:active {
          background: #f1f5f9;
          color: #111827;
        }

        .gh-mobile-drawer-footer {
          padding: 0.9rem 1rem max(1rem, env(safe-area-inset-bottom));
          border-top: 1px solid rgba(15, 23, 42, 0.08);
          background: rgba(255, 255, 255, 0.94);
          backdrop-filter: blur(10px);
          display: grid;
          gap: 0.8rem;
        }

        .gh-mobile-theme-toggle {
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #f8fafc;
          border-radius: 999px;
          padding: 0.38rem 0.55rem 0.38rem 0.42rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.7rem;
          cursor: pointer;
        }

        .gh-mobile-theme-track {
          width: 58px;
          height: 32px;
          background: linear-gradient(160deg, #fde68a, #f8fafc 60%);
          border-radius: 999px;
          position: relative;
          padding: 2px;
          border: 1px solid rgba(15, 23, 42, 0.1);
        }

        .gh-mobile-theme-thumb {
          width: 26px;
          height: 26px;
          border-radius: 999px;
          background: #0f172a;
          color: #ffffff;
          display: grid;
          place-items: center;
          transform: translateX(0);
          transition: transform 0.24s ease;
          position: relative;
          overflow: hidden;
        }

        .gh-mobile-theme-toggle.is-light .gh-mobile-theme-thumb {
          transform: translateX(0);
        }

        .gh-mobile-theme-toggle.is-dark .gh-mobile-theme-track {
          background: linear-gradient(160deg, #1f2937, #0f172a 70%);
        }

        .gh-mobile-theme-toggle.is-dark .gh-mobile-theme-thumb {
          transform: translateX(26px);
          background: #f8fafc;
          color: #0f172a;
        }

        .gh-theme-icon {
          position: absolute;
          width: 14px;
          height: 14px;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }

        .gh-theme-sun {
          opacity: 1;
          transform: translateY(0);
        }

        .gh-theme-moon {
          opacity: 0;
          transform: translateY(10px);
        }

        .gh-mobile-theme-toggle.is-dark .gh-theme-sun {
          opacity: 0;
          transform: translateY(-10px);
        }

        .gh-mobile-theme-toggle.is-dark .gh-theme-moon {
          opacity: 1;
          transform: translateY(0);
        }

        .gh-mobile-theme-label {
          font-size: 0.92rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: 0.01em;
          padding-right: 0.35rem;
        }

        .gh-mobile-language-switch {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.55rem;
        }

        .gh-mobile-language-switch button {
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #fff;
          color: #334155;
          border-radius: 10px;
          height: 38px;
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
        }

        .gh-mobile-language-switch button.active {
          border-color: #0f172a;
          color: #0f172a;
          background: #eef2ff;
        }

        [data-theme="dark"] .gh-mobile-drawer-panel {
          background: linear-gradient(170deg, #0f172a 0%, #111827 50%, #1f2937 100%);
          border-left-color: rgba(255, 255, 255, 0.1);
          box-shadow: -16px 0 38px rgba(0, 0, 0, 0.45);
        }

        [data-theme="dark"] .gh-mobile-drawer-header {
          background: rgba(15, 23, 42, 0.86);
          border-bottom-color: rgba(255, 255, 255, 0.12);
        }

        [data-theme="dark"] .gh-mobile-drawer-header h2,
        [data-theme="dark"] .gh-mobile-drawer-row > a,
        [data-theme="dark"] .gh-mobile-theme-label {
          color: #f8fafc;
        }

        [data-theme="dark"] .gh-mobile-drawer-close,
        [data-theme="dark"] .gh-mobile-drawer-item,
        [data-theme="dark"] .gh-mobile-theme-toggle,
        [data-theme="dark"] .gh-mobile-language-switch button {
          background: rgba(30, 41, 59, 0.75);
          border-color: rgba(255, 255, 255, 0.14);
          color: #e5e7eb;
        }

        [data-theme="dark"] .gh-mobile-drawer-row > a:hover,
        [data-theme="dark"] .gh-mobile-drawer-submenu a:hover {
          background: rgba(51, 65, 85, 0.7);
        }

        [data-theme="dark"] .gh-mobile-drawer-submenu > ul,
        [data-theme="dark"] .gh-mobile-drawer-footer {
          border-top-color: rgba(255, 255, 255, 0.12);
        }

        [data-theme="dark"] .gh-mobile-drawer-submenu a {
          color: #cbd5e1;
        }

        [data-theme="dark"] .gh-mobile-language-switch button.active {
          background: rgba(15, 23, 42, 0.95);
          color: #ffffff;
          border-color: rgba(255, 255, 255, 0.3);
        }

        [data-theme="dark"] .gh-mobile-drawer-footer {
          background: rgba(15, 23, 42, 0.92);
        }

        @media (min-width: 769px) {
          .gh-mobile-drawer-react {
            display: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .gh-mobile-drawer-backdrop,
          .gh-mobile-drawer-panel,
          .gh-mobile-drawer-submenu,
          .gh-mobile-drawer-submenu > ul,
          .gh-mobile-theme-thumb,
          .gh-theme-icon {
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default MobileMenuDrawer;
